import React from 'react';
import { IconExclamationTriangle, IconFilter, IconSort, IconSortDown, IconSortUp } from '../ui-kit/icons';
import lodash from 'lodash';
import { Dropdown, Form } from '../ui-kit';
import CandidatesSelectTableRow from './CandidatesSelectTableRow';

export const filterFullCandidates = candidates =>
  candidates.filter(
    candidate => candidate.can_fully_take_over && !candidate.back_to_back && !candidate.unavailable_for_duty_request
  );

const filterPartialCandidates = candidates =>
  candidates.filter(
    candidate => !candidate.can_fully_take_over && !candidate.back_to_back && !candidate.unavailable_for_duty_request
  );

const filterUnavailableForDutyRequestCanidates = candidates =>
  candidates.filter(candidate => candidate.unavailable_for_duty_request);

const filterBackToBackCandidates = candidates =>
  candidates.filter(candidate => candidate.back_to_back && !candidate.unavailable_for_duty_request);

const CandidatesSelectTable = props => {
  const filterByGroups = candidates =>
    candidates.filter(candidate =>
      candidate.groups.find(
        group =>
          // filter on group id, and if user selected to filter on available only,
          // also select those who are available for that station
          props.selectedGroups.includes(Number(group.id)) && (!props.filterAvailableOnly || group.available_time > 0)
      )
    );

  const renderCandidatesList = candidates =>
    candidates.map(candidate => (
      <CandidatesSelectTableRow
        candidate={candidate}
        skills={props.skills}
        availabilityCodes={props.availabilityCodes}
        dutyFunctions={props.dutyFunctions}
        key={candidate.user.id}
        requestType={props.requestType}
        selectedCandidates={props.selectedCandidates}
        onChange={props.onChange}
      />
    ));

  const renderCandidates = candidates => {
    // If there is no balance sorting, show normal order
    if (props.sortedBalance !== -1) {
      candidates = lodash.orderBy(candidates, [
        ({ contractStatistics }) => contractStatistics && contractStatistics.balance
      ]);
      // If sortBalance is 1, return in ascending order (no balance first, the from lowest to highest),
      // else return in descending order (from highest to lowest, no balance last)
      candidates = props.sortedBalance === 1 ? candidates.reverse() : candidates;
    }

    const filteredCandidates = filterByGroups(candidates);
    const partialCandidates = filterPartialCandidates(filteredCandidates);
    const unavailableForDutyRequestCandidates = filterUnavailableForDutyRequestCanidates(filteredCandidates);
    const backToBackCandidates = filterBackToBackCandidates(filteredCandidates);
    return (
      <>
        {renderCandidatesList(filterFullCandidates(filteredCandidates))}
        {partialCandidates.length > 0 && (
          <>
            <tr>
              <th colSpan="7" className="text-center">
                {I18n.t('schedules.exchanges.duty_exchange.candidates.can_take_over_partially')}
              </th>
            </tr>
            {renderCandidatesList(partialCandidates)}
          </>
        )}
        {unavailableForDutyRequestCandidates.length > 0 && (
          <>
            <tr>
              <th colSpan="7" className="text-center">
                <IconExclamationTriangle size="lg" className="warning" style={{ color: '#f0ad4e' }} />
                {I18n.t('schedules.exchanges.duty_exchange.candidates.not_suitable')}
              </th>
            </tr>
            {renderCandidatesList(unavailableForDutyRequestCandidates)}
          </>
        )}
        {backToBackCandidates.length > 0 && (
          <>
            <tr>
              <th colSpan="7" className="text-center">
                <IconExclamationTriangle size="lg" className="warning" style={{ color: '#f0ad4e' }} />
                {I18n.t('schedules.exchanges.duty_exchange.candidates.back_to_back')}
              </th>
            </tr>
            {renderCandidatesList(backToBackCandidates)}
          </>
        )}
      </>
    );
  };

  const renderNoResultsFound = colspan => (
    <tr>
      <td className="none" colSpan={colspan}>
        {I18n.t('no_results_found')}
      </td>
    </tr>
  );

  const renderGroupsFilter = () => (
    <Dropdown.Menu>
      <Dropdown.Item eventKey="filterAvailableOnly">
        <Form.Check
          id="filterAvailableOnly"
          checked={props.filterAvailableOnly}
          value="filterAvailableOnly"
          onChange={props.toggleFilterAvailableOnly}
          label={I18n.t('schedules.exchanges.duty_exchange.candidates.show_available_only')}
        />
      </Dropdown.Item>
      <Dropdown.Divider />
      <Dropdown.Item eventKey="all">{I18n.t('schedules.exchanges.duty_exchange.candidates.select_all')}</Dropdown.Item>
      <Dropdown.Item eventKey="none">
        {I18n.t('schedules.exchanges.duty_exchange.candidates.select_none')}
      </Dropdown.Item>
      {props.groups.map(group => (
        <Dropdown.Item eventKey={group.id} key={group.id}>
          <Form.Check
            id={`selected_${group.id}`}
            checked={props.selectedGroups.includes(Number(group.id))}
            value={group.id}
            label={group.name}
          />
        </Dropdown.Item>
      ))}
    </Dropdown.Menu>
  );

  const handleToggle = () => {
    props.toggleDropdown();
  };

  return (
    <table className="table candidates-table">
      <thead className="align-middle">
        <tr>
          <th>{I18n.t('attributes.name')}</th>
          <th>{I18n.t('activerecord.attributes.user.skill_icons')}</th>
          <th>
            {I18n.t('member_of')}{' '}
            <Dropdown
              onSelect={props.filterGroups}
              id="groups-filter-dropdown"
              open={props.dropdownOpen}
              onToggle={handleToggle}
              className="d-inline ms-2"
            >
              <Dropdown.Toggle size="sm" variant="secondary">
                <IconFilter />
              </Dropdown.Toggle>
              {renderGroupsFilter()}
            </Dropdown>
          </th>
          <th>{I18n.t('activerecord.attributes.schedule/schedule_exception.comments')}</th>
          <th
            className="contract-progress"
            dangerouslySetInnerHTML={{
              __html: I18n.t('activerecord.attributes.reporting/contracts/contract.fulfillment_ratio_colored')
            }}
          />
          <th
            className="contract-balance"
            onClick={() =>
              // allow disabling sorting by balance by fully cicling between -1, 0 and 1
              props.sortBalance(props.sortedBalance === -1 ? 0 : props.sortedBalance === 0 ? 1 : -1)
            }
          >
            {I18n.t('activerecord.attributes.reporting/contracts/contract.balance')}{' '}
            {props.sortedBalance === -1 && <IconSortDown />}
            {props.sortedBalance === 1 && <IconSortUp />}
            {props.sortedBalance === 0 && <IconSort />}
          </th>
          <th className="contract-constraints" />
        </tr>
      </thead>
      <tbody id="candidates_select_container">
        {props.candidates.length > 0 ? renderCandidates(props.candidates) : renderNoResultsFound(6)}
      </tbody>
    </table>
  );
};

export default CandidatesSelectTable;
